import { z } from 'zod';

// BEMORE: the applicant-form question and answer contracts are DEFINED ONCE, in the api-client, and
// reused here. Restating them would give the anonymous signup page and the admin builder two copies
// of one wire shape — and a drift between them shows up as an option list that renders empty, which
// reads like "the admin forgot to add options" rather than like a contract mismatch.
import type { RoleApplicationAnswer } from '@zercode/api-client';
import { roleApplicationQuestionSchema } from '@zercode/api-client';

/**
 * Runtime (Zod) validation of Identity responses [STD-FE].
 * Compile-time wire types are generated from the Identity OpenAPI contract
 * (@zercode/api-client/generated/identity — run `pnpm generate:api`); these
 * schemas validate the untrusted runtime payloads and are kept in sync with
 * that contract.
 *
 * 2026-07-06: gerçek sözleşmeyle hizalandı — login TokenPairResult döner
 * (user İÇERMEZ; kullanıcı /api/v1/users/me'den alınır: MeResult).
 */

/** Identity MeResult: { id, email, displayName, status, roles, permissions } */
export const authUserSchema = z.object({
  id: z.string(),
  /**
   * ⚠ NULLISH since STU-HANDLE (mig 314), and this is a HARD requirement, not defensive typing. A student a
   * teacher created without an address has `email: null`, and a required `z.string()` here made
   * `authUserSchema.parse()` throw on their very first `/users/me` — which is the call that bootstraps the
   * session, so the entire app failed to load for exactly the persona the feature exists for. There is no
   * fallback: read `hasEmail` to decide whether to prompt, and render `displayName` where a name is wanted.
   */
  email: z.string().nullish(),
  /**
   * The COMPOSED name — Identity's generated `full_name` coalesced to the e-mail (USR-PARTS, mig 311).
   * This is what every surface RENDERS; it is never edited directly. The parts below are what a form binds
   * to. The field name is unchanged on purpose, so no read site needed touching during the cutover.
   */
  displayName: z.string().optional(),
  // USR-PARTS (mig 311): the caller's own name parts, for the Account Center name form. Nullish because an
  // older backend omits them and because last/middle are genuinely optional on legacy rows (a user whose
  // pre-311 display name was a single token has no surname, and none was invented for them).
  firstName: z.string().nullish(),
  middleName: z.string().nullish(),
  lastName: z.string().nullish(),
  avatarUrl: z.string().nullish(), // P1-3a: MeResult.avatarUrl (header + Account Center)
  phone: z.string().nullish(),     // S-12.b: MeResult.phone (Account Center; SMS kanalı)
  phoneVerified: z.boolean().optional(),
  status: z.string().optional(),
  roles: z.array(z.string()).default([]),
  permissions: z.array(z.string()).default([]),
  // BE-3/FE-3: server-computed SPA surfaces this user can reach (unified-login routing). Defaults to
  // [] so an older backend without the field still parses; FE-3 treats [] as "no app access".
  reachableApps: z.array(z.string()).default([]),
  // RSU: the ONE surface login should land on when reachableApps holds several (roles accumulate —
  // an approved Partner Employee keeps 'student', so multi-surface is the normal case). Always an
  // element of reachableApps, guaranteed server-side. Nullish so an older backend without the field
  // still parses, in which case resolveAuthOriginRoute falls back to the persona picker as before.
  defaultApp: z.string().nullish(),
  // UP2: admin-assigned references, resolved to names server-side so the self Profile can show them
  // read-only (the user can't read the companies/users catalogs). Nullish — older backends omit them.
  companyId: z.string().nullish(),
  partnerName: z.string().nullish(),
  // SELF-SCHOOL: the id is what a picker preselects; the name alone could only be displayed.
  // AVATAR-UPLOAD (mig 309): PRESENCE only — the storage key is tenant-prefixed and never leaves the
  // server, so the bytes come from GET /users/me/avatar and this says whether to ask.
  // USR-NAME (mig 310): the caller's optional second login identifier. Null = sign in with the e-mail.
  username: z.string().nullish(),
  // USER-CODE (mig 629): the caller's own 9-character code (profile hero card). Null until the tenant has a prefix.
  userCode: z.string().nullish(),
  /**
   * STU-HANDLE (mig 314): does this account have an address AT ALL, and has it been proven?
   *
   * ⚠ `hasEmail` is deliberately a separate field rather than `email != null`. It is the server's answer to
   * "should this user be asked to add an address", which is a product decision the client must not re-derive
   * — a future rule (an address supplied by a guardian, say) would change the answer without changing the
   * column.
   *
   * ⚠ `emailVerified` is independent of `status`. A teacher-created student is `active` AND unverified at the
   * same time — that is the whole point of the slice — so gating anything on `status` would either lock out a
   * working account or nag a verified one. The deferred account lock will read THIS field.
   *
   * Nullish so an older backend without them still parses; a missing value must be treated as "don't nag",
   * never as "unverified", or every user on a stale server gets a banner they cannot dismiss.
   */
  hasEmail: z.boolean().nullish(),
  emailVerified: z.boolean().nullish(),
  /**
   * STU-HANDLE (mig 314): is this user still signing in with the one-time password a TEACHER typed for them?
   * Cleared the moment they change it (POST /users/me/password).
   *
   * ⚠ Nullish, and a missing value means "don't nag" — same rule as the two flags above. Nothing is BLOCKED on
   * it: the only place to change the password is inside the app, so locking on it would strand the account.
   */
  mustChangePassword: z.boolean().nullish(),
  /**
   * PROF-REQ: which REQUIRED profile fields the caller has not filled in — the input to the "complete your
   * profile" banner. Values are the camel-case FIELD NAMES the forms already bind to (`firstName`,
   * `birthDate`, `genderDefinitionId`, …), so a SPA can point at the control rather than translate a code.
   *
   * ⚠ The SERVER owns the list (Identity's ProfileCompleteness), for the same reason `hasEmail` exists rather
   * than testing `email != null`: three SPAs show this prompt and two also render the form behind it, so a
   * client-side rule would drift and the banner would disagree with the form it points at.
   *
   * Defaults to `[]` so an older backend without the field still parses AND still says "nothing to ask" —
   * same "a missing value must never nag" rule as the three flags above.
   */
  missingProfileFields: z.array(z.string()).default([]),
  hasAvatar: z.boolean().nullish(),
  schoolId: z.guid().nullish(),
  schoolName: z.string().nullish(),
  // STU-TCH: the caller's own teacher — id AND name. The id is what the self-profile picker
  // preselects; without it the screen could only DISPLAY a teacher, never offer a choice.
  teacherUserId: z.guid().nullish(),
  teacherName: z.string().nullish(),
  teeneagleParentUserId: z.guid().nullish(),
  teeneagleParentName: z.string().nullish(),
  // SCH-independent: the assigned school's partner (id + resolved name), surfaced so the self Profile
  // can pre-fill it as the DEFAULT for the user's (now independent) partner selection.
  schoolPartnerId: z.guid().nullish(),
  schoolPartnerName: z.string().nullish(),
  // SP: the user's own (independent) salesperson (id + resolved name), plus the user's school's
  // salesperson — surfaced so the self Profile can pre-fill the school's salesperson as the DEFAULT.
  salespersonId: z.guid().nullish(),
  salespersonName: z.string().nullish(),
  schoolSalespersonId: z.guid().nullish(),
  schoolSalespersonName: z.string().nullish()
});

export type AuthUser = z.infer<typeof authUserSchema>;

/**
 * SCH-independent: one selectable partner company for the self-service partner picker
 * (GET /users/me/assignable-partners → live companies of type 'partner').
 */
export const partnerOptionSchema = z.object({
  id: z.guid(),
  legalName: z.string()
});

export type PartnerOption = z.infer<typeof partnerOptionSchema>;

/**
 * PPT-2: one of the caller's per-program-type partner choices
 * (GET /users/me/partner-selections).
 *
 * `programTypeId` is an OPAQUE definitions id (group `program_types`) — the same identifier space as
 * an event's programTypeId. The LABEL is not on this contract on purpose: it is tenant-editable and
 * belongs to Definitions, so the UI resolves it from that group rather than trusting a copy here
 * [STD-OWN]. A selection whose partner was soft-deleted is simply absent from the list.
 */
export const partnerSelectionSchema = z.object({
  programTypeId: z.guid(),
  companyId: z.guid(),
  companyName: z.string()
});

export type PartnerSelection = z.infer<typeof partnerSelectionSchema>;

/**
 * PPT-SCH: what the caller's own school routes a program type to — the DEFAULT they inherit.
 *
 * Same shape as a personal selection, different meaning: this is the school's routing, not the
 * student's choice. The UI compares the two to say either "this came from your school" or "your
 * school routes this to X" when the student has picked something else.
 */
export const schoolPartnerSelectionSchema = z.object({
  programTypeId: z.guid(),
  companyId: z.guid(),
  companyName: z.string()
});

export type SchoolPartnerSelection = z.infer<typeof schoolPartnerSelectionSchema>;

/**
 * SP: one selectable user for the self-service salesperson picker
 * (GET /users/me/salesperson-options → live users).
 */
export const userOptionSchema = z.object({
  id: z.guid(),
  displayName: z.string()
});

export type UserOption = z.infer<typeof userOptionSchema>;

/**
 * SCH-PICK: one selectable school for the self-service school picker
 * (GET /users/me/school-options → live schools in the caller's country, SEARCHED and capped at 50).
 *
 * ⚠ IT IS `userOptionSchema` PLUS A CITY, and the city is not decoration: 602 of the 11,779 schools on
 * the demo tenant share their name with another school, so a bare-name row asks the user to choose
 * between two identical lines. The id is an OPAQUE `definitions.definitions.id` — Identity holds no grant
 * on that schema and cannot resolve it [STD-OWN] — so the page resolves the label the same way its
 * country picker does. `nullish`, not required: one school with no city must not fail the whole list.
 */
export const schoolOptionSchema = userOptionSchema.extend({
  cityDefinitionId: z.string().nullish()
});

export const schoolOptionListSchema = z.array(schoolOptionSchema);

export type SchoolOption = z.infer<typeof schoolOptionSchema>;

/** Identity TokenPairResult — login VE refresh aynı şekli döner. */
export const tokenPairSchema = z.object({
  accessToken: z.string(),
  tokenType: z.string().optional(),
  accessTokenExpiresAtUtc: z.string().optional(),
  /**
   * TODO(security): move the refresh token to an httpOnly cookie set by
   * Identity and drop it from the JSON body entirely. Until then it is kept
   * ONLY in memory (Zustand, non-persisted) — never localStorage.
   */
  refreshToken: z.string().optional(),
  refreshTokenExpiresAtUtc: z.string().optional(),
  sessionId: z.string().optional()
});

export type TokenPair = z.infer<typeof tokenPairSchema>;
export type LoginResponse = TokenPair;
export type RefreshResponse = TokenPair;

/**
 * LOGIN-LINK (mig 559): what redeeming an admin-minted sign-in link returns — an ACCESS TOKEN ONLY.
 * There is no refresh token by design; the token's expiry is the link's own deadline.
 */
export const loginLinkSessionSchema = z.object({
  accessToken: z.string(),
  tokenType: z.string().optional(),
  accessTokenExpiresAtUtc: z.string(),
  sessionId: z.string(),
  userId: z.string()
});

export type LoginLinkSession = z.infer<typeof loginLinkSessionSchema>;

/** @deprecated tokenPairSchema kullanın — geriye uyumluluk için tutuluyor. */
export const loginResponseSchema = tokenPairSchema;
export const refreshResponseSchema = tokenPairSchema;

/**
 * Identity MfaChallengeDto (P1-3c-2) — login MFA'lı kullanıcıda token yerine bunu döner.
 * challengeToken 5 dk ömürlü, ayrı-audience JWT; kod /auth/mfa'da doğrulanır.
 */
export const mfaChallengeSchema = z.object({
  challengeToken: z.string(),
  methods: z.array(z.string()).default([]),
  expiresAtUtc: z.string()
});

export type MfaChallenge = z.infer<typeof mfaChallengeSchema>;

/**
 * Login iki sonuçtan birini döner: token çifti (MFA yok) veya challenge (MFA var).
 * Ayrım gövdedeki alandan: challengeToken varsa MFA adımı gerekir.
 * (expand/contract — eski sözleşme TokenPairResult'la bayt-bayt uyumlu.)
 */
export type LoginResult =
  | { kind: 'authenticated'; user: AuthUser }
  | { kind: 'mfa'; challenge: MfaChallenge };

/** POST /auth/mfa gövdesi — challenge + 6 haneli TOTP veya 10 haneli yedek kod. */
export const mfaCompletionSchema = z.object({
  challengeToken: z.string().min(1),
  code: z.string().regex(/^(\d{6}|\d{10})$/, 'Enter a 6-digit code or a 10-digit backup code.')
});

export type MfaCompletion = z.infer<typeof mfaCompletionSchema>;

/**
 * USR-NAME (mig 310): ONE field that accepts an e-mail OR a username — the server discriminates by
 * shape, and '@' is excluded from legal usernames precisely so it can.
 *
 * ⚠ The old rule was `email: z.string().email()`, which would reject every username before the request
 * left the browser. Validation here is deliberately shallow: non-empty, and an e-mail shape check ONLY
 * when the value contains '@'. Anything stricter would re-implement the server's rules in a second place
 * and start rejecting handles the server happily accepts.
 */
export const loginCredentialsSchema = z.object({
  identifier: z
    .string()
    .min(1, 'Enter your email address or username.')
    .refine((v) => !v.includes('@') || z.string().email().safeParse(v).success, 'Enter a valid email address.'),
  password: z.string().min(1)
});

/**
 * USR-NAME: the availability answer. Echoes the NORMALISED handle so a client can tell WHICH handle was
 * answered about — 'Ahmet' asked, 'ahmet' answered — and drop a reply that arrived out of order while the
 * user kept typing.
 */
export const usernameAvailabilitySchema = z.object({
  username: z.string(),
  available: z.boolean()
});

export type UsernameAvailability = z.infer<typeof usernameAvailabilitySchema>;

export type LoginCredentials = z.infer<typeof loginCredentialsSchema>;

/**
 * SR: self-registration credentials. Password rule mirrors the backend ChangePassword
 * policy (min 12, max 128).
 *
 * USR-PARTS (mig 311): the name is firstName + lastName, both REQUIRED — inverted from the previous
 * shape, where one required `displayName` sat beside three optional parts. The server requires first
 * and last TOGETHER (RegisterCommandValidator) and never requires middleName, so middleName stays
 * optional here and is deliberately absent from the signup FORM — it is edited later in Account
 * Center, because signup is the highest-friction form in the product.
 *
 * ⚠ `displayName` is GONE from this schema. The API still accepts it and splits it via
 * NameParts.Resolve — that compatibility path is what kept signup working through the backend
 * cutover — but a client that HAS the two fields must not send both, or the split becomes dead code
 * that nothing exercises and everything silently depends on.
 *
 * The 100-char caps mirror ck_user_profiles_name_parts_length, so a value accepted here cannot be
 * refused by the database.
 */
export const registerCredentialsSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8, 'Password must be at least 8 characters.').max(128, 'Password must be at most 128 characters.'),
  firstName: z.string().min(1, 'First name is required.').max(100, 'First name must be at most 100 characters.'),
  lastName: z.string().min(1, 'Last name is required.').max(100, 'Last name must be at most 100 characters.'),
  middleName: z.string().max(100, 'Middle name must be at most 100 characters.').optional(),
  // RSU: which kind of account this is. Mirrors the backend allow-list exactly — a client cannot
  // name any other rbac role here (the server re-validates and 422s).
  requestedRole: z.enum(['student', 'teacher', 'partner_employee']).optional(),
  // RSU: ONE FIELD, TWO MEANINGS, because identity.users.company_id has two:
  //   student / teacher   -> "the partner I chose"          — applied immediately
  //   partner_employee    -> "the partner I work for"       — applied only when an admin approves
  // Required by the server for partner_employee; optional for the other two.
  requestedCompanyId: z.string().optional(),
  /**
   * SIGNUP-COUNTRY: the applicant's country — an OPAQUE `definitions.definitions.id` from the group
   * `country`, the same identifier space `identity.user_profiles.country_definition_id` uses. Asked of
   * EVERY role, unlike `requestedCompanyId` above.
   *
   * ⚠ `.optional()` here describes the WIRE contract, not the form. The server accepts a registration
   * with no country (so a stale SPA build does not start failing), but the signup form makes the field
   * mandatory for all three roles — see RegisterPage's per-role schema. Do not "simplify" this to
   * required: that would make the two disagree, and the disagreement would only show up as a 400 from a
   * client nobody redeployed.
   */
  countryDefinitionId: z.string().optional(),
  /**
   * SIGNUP-SCHOOL: the applicant's school — an `identity.schools` id, from the anonymous list scoped to
   * `countryDefinitionId`.
   *
   * ⚠ `.optional()` describes the WIRE, exactly as `countryDefinitionId` above does, and for the same
   * compatibility reason. The STUDENT form makes it mandatory (see RegisterPage's per-role schema);
   * teacher and partner_employee never send it.
   *
   * ⚠ THE SERVER CHECKS THE SCHOOL IS IN `countryDefinitionId` and 422s otherwise, so the two must be
   * sent as a PAIR. Never send a school carried over from a previously selected country.
   */
  schoolId: z.string().optional(),
  /**
   * SIGNUP-BIRTHDATE: the applicant's date of birth as `yyyy-MM-dd`, landing on
   * `identity.user_profiles.birth_date`.
   *
   * ⚠ `.optional()` describes the WIRE, exactly as `countryDefinitionId` and `schoolId` above do and
   * for the same compatibility reason. The STUDENT form makes it mandatory (see RegisterPage's per-role
   * schema); teacher and partner_employee never send it.
   *
   * ⚠ THE REGEX IS NOT DECORATION. The server binds this to a `DateOnly`, so anything it cannot parse
   * is a 400 naming a field the visitor did not type wrong — `<input type="date">` yields either '' or
   * a valid ISO date, and '' must be dropped before it is sent (onSubmit does that), never posted.
   * A date is NOT range-checked here: the plausible-human window belongs to one owner, and that owner
   * is the server (RegisterCommandValidator), which the form cannot drift from.
   */
  birthDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Birth date must be an ISO date (yyyy-MM-dd).')
    .optional(),
  /**
   * SIGNUP-GENDER: the applicant's gender — an OPAQUE `definitions.definitions.id` from the group
   * `genders`, the same identifier space `identity.user_profiles.gender_definition_id` holds.
   *
   * ⚠ `.optional()` describes the WIRE, exactly as `countryDefinitionId`, `schoolId` and `birthDate`
   * above do and for the same compatibility reason. The STUDENT and TEACHER forms make it mandatory
   * (see RegisterPage's per-role schema); partner_employee never sends it.
   *
   * ⚠ SEND THE ID, NEVER THE CODE. 'female' is a `code` column and this binds to a `Guid?` — a code
   * posted here is a 400 from the model binder, on a field the visitor filled in correctly.
   *
   * ⚠ NO SET-MEMBERSHIP CHECK EXISTS SERVER-SIDE and none can: Identity has no grant on the
   * `definitions` schema [STD-OWN], so it validates format only (and refuses `Guid.Empty`). Only ever
   * send an id the public genders list gave you.
   */
  genderDefinitionId: z.string().optional(),
  /**
   * AGECAT-SEED: the applicant's age-band placements, one per program type, derived from `birthDate`
   * against the public band catalogue (`usePublicAgeCategories` → `derivePlacements`).
   *
   * ⚠ THE CLIENT DERIVES SOMETHING THE SERVER STORES, and that is the architecture rather than a
   * shortcut: `definitions.age_categories` belongs to another service and Identity holds no grant on
   * that schema [STD-OWN], so it can neither compute a placement nor check one. The same standing
   * `countryDefinitionId` and `genderDefinitionId` above already have — send only ids the public
   * catalogue gave you.
   *
   * ⚠ `.optional()` describes the WIRE, exactly as the four fields above do and for the same
   * compatibility reason. Only the roles that ask for a birth date send it; for the others the form
   * has nothing to derive from and omits the field.
   *
   * ⚠ ABSENT AND `[]` MEAN THE SAME THING HERE, unlike on `UserDemographicsRequest`, where absent
   * means "leave the stored set alone" and `[]` means "clear it". An account being created has no
   * stored set, so there is nothing to leave alone. Do not copy this equivalence to the profile save.
   */
  ageCategories: z
    .array(z.object({ programTypeId: z.string(), ageCategoryId: z.string() }))
    .optional(),
  /**
   * SIGNUP-SCHOOL-NEW: the school the visitor DESCRIBED, for when the picker holds no row for it. The
   * server creates it `status='pending'` — invisible to every picker until an administrator approves it
   * — and attaches the new account to it.
   *
   * ⚠ MUTUALLY EXCLUSIVE WITH `schoolId`. They are two answers to one question and the server 400s a
   * payload carrying both, deliberately: choosing a winner would attach the student to a school they
   * did not pick. Send one, never the other alongside it.
   *
   * ⚠ NO `country` FIELD, and it is not an omission. The school is created in `countryDefinitionId`
   * above — the same country the picker was scoped by. A second country here could disagree with it,
   * and the student's own profile country would then contradict their school's.
   *
   * `cityDefinitionId` / `districtDefinitionId` are OPAQUE definitions ids (groups `city` / `district`),
   * the same identifier space the school picker's labels resolve. The server stores them without
   * checking membership — it cannot [STD-OWN] — so only ever send ids the public lists gave you, and
   * never send a district without its city (the server 400s that pair: a district name identifies
   * nothing on its own).
   */
  proposedSchool: z
    .object({
      name: z.string().min(1, 'School name is required.').max(200, 'School name must be at most 200 characters.'),
      cityDefinitionId: z.string().optional(),
      districtDefinitionId: z.string().optional()
    })
    .optional()
});

export type RegisterCredentials = z.infer<typeof registerCredentialsSchema>;

/** RSU: the roles a visitor may pick on the signup chooser. */
export type SignupRole = NonNullable<RegisterCredentials['requestedRole']>;

/**
 * RSU: one partner company as the ANONYMOUS signup form may see it (GET /auth/partners).
 *
 * ⚠ TWO FIELDS, AND IT MUST STAY THAT WAY. The server deliberately does not serve its full company
 * projection here — that one carries the negotiated commercial terms (commission, incentive, risk
 * band) and this endpoint needs no token. Do not widen this schema to "reuse" the company DTO.
 */
export const publicPartnerSchema = z.object({
  id: z.string(),
  name: z.string()
});

export const publicPartnerListSchema = z.array(publicPartnerSchema);

export type PublicPartner = z.infer<typeof publicPartnerSchema>;

/**
 * BEMORE: one role a visitor may APPLY for, as the anonymous /register/bemore page sees it
 * (GET /api/v1/auth/role-application-forms on Identity).
 *
 * ⚠ THE QUESTION SHAPE IS SHARED WITH EVENT QUESTIONS ON PURPOSE, so one renderer serves both — it is
 * reused from `@zercode/api-client` rather than restated here, because two copies of a wire contract
 * drift and the drift is invisible until an option list renders empty.
 *
 * ⚠ NO ROLE CODE AND NO ROLE TYPE, deliberately, and this must stay that way for the same reason
 * `publicPartnerSchema` stays two fields wide: an unauthenticated caller gets what it takes to render
 * and submit the form, never a description of how the tenant is configured.
 */
export const publicRoleApplicationFormSchema = z.object({
  roleId: z.string(),
  /**
   * BEMORE-LINK: the role's stable machine code, what `/register/bemore?role=camp_guide` matches on.
   *
   * ⚠ Nullish so a response from a pre-BEMORE-LINK backend still parses — the page falls back to
   * matching the id, then the display name, so an older server degrades to "the link needs the uuid"
   * rather than to a blank form.
   */
  roleCode: z.string().nullish(),
  roleName: z.string(),
  /** The one-liner under the role in the picker. */
  description: z.string().nullish(),
  /** The information panel above the questions — the tenant's own "what this role does". */
  infoTitle: z.string().nullish(),
  infoBody: z.string().nullish(),
  questions: z.array(roleApplicationQuestionSchema)
});

export const publicRoleApplicationFormListSchema = z.array(publicRoleApplicationFormSchema);

export type PublicRoleApplicationForm = z.infer<typeof publicRoleApplicationFormSchema>;

/**
 * BEMORE: what the Be More form posts.
 *
 * ⚠ NO PASSWORD, and that is the shape of the whole feature rather than an omission: no account is
 * created at submit, so there is no credential to set. Approval mints an activation link instead.
 *
 * Each answer carries only `questionId` and `value`; the label and type stored beside it are taken
 * from the live question server-side, so a client cannot choose the wording it is filed under.
 */
export interface RoleApplicationSubmission {
  roleId: string;
  firstName: string;
  lastName: string;
  email: string;
  answers: RoleApplicationAnswer[];
}

/**
 * SIGNUP-SCHOOL: one school as the ANONYMOUS student signup form may see it
 * (GET /api/v1/auth/schools?countryDefinitionId= on Identity).
 *
 * ⚠ TWO FIELDS, AND IT MUST STAY THAT WAY — the same rule as `publicPartnerSchema` above, for a
 * sharper reason: the authenticated school DTO carries the tax id, registration number, the full
 * address block and a named contact person's e-mail and phone. This endpoint needs no token. Do not
 * widen this schema to "reuse" `schoolSchema` from @zercode/api-client.
 */
export const publicSchoolSchema = z.object({
  id: z.string(),
  name: z.string(),
  /**
   * SIGNUP-LABEL: the school's city and district, as OPAQUE definition ids — Identity holds no grant
   * on the definitions schema and cannot resolve them to names [STD-OWN]. The form resolves them the
   * same way it resolves the country picker, and uses them only to tell two same-named schools apart.
   *
   * `nullish`, not required: a school with no district is ordinary, and a required field here would
   * fail to parse the WHOLE list rather than leave one label short.
   */
  cityDefinitionId: z.string().nullish(),
  districtDefinitionId: z.string().nullish()
});

export const publicSchoolListSchema = z.array(publicSchoolSchema);

export type PublicSchool = z.infer<typeof publicSchoolSchema>;

/**
 * SIGNUP-COUNTRY: one reference-data value as the ANONYMOUS signup form may see it
 * (GET /api/v1/public/definitions/{key}/items on the **Definitions** service, :5975).
 *
 * ⚠ NOT the same shape as `definitionSchema` in @zercode/api-client, and it must not be replaced by it.
 * That one mirrors the authenticated `DefinitionDto` — `metadata` (free-form tenant jsonb), `isSystem`,
 * `isDefault`, audit stamps — and the server deliberately serves none of it without a token. A schema
 * that expected those fields would fail to parse a perfectly good public response.
 *
 * ⚠ It comes from a DIFFERENT SERVICE than every other schema in this file. Identity cannot serve this
 * list: only `svc_definitions` may read the `definitions` schema, so the signup page talks to two
 * origins — Identity for the POST, Definitions for the picker.
 */
export const publicDefinitionSchema = z.object({
  id: z.guid(),
  code: z.string(),
  name: z.string(),
  // Null for a flat group like `country`; present so a nested public group (country → city) needs no
  // second contract.
  parentId: z.guid().nullish()
});

export const publicDefinitionListSchema = z.array(publicDefinitionSchema);

export type PublicDefinition = z.infer<typeof publicDefinitionSchema>;

/**
 * AGECAT-SEED: one age band as the ANONYMOUS signup form may see it
 * (GET /api/v1/public/definitions/age-categories on the **Definitions** service, :5975).
 *
 * The form derives the applicant's placement per programme from these and their birth date, and posts
 * the result as `registerCredentials.ageCategories` — so a student is in their cohort from the moment
 * the account exists, instead of from the first time they open their profile page.
 *
 * ⚠ NOT a definition GROUP, which is why it is not `publicDefinitionSchema`. Bands live in their own
 * table with bounds and a programme; the four columns that schema serves could not carry them.
 *
 * ⚠ NO `isActive`, and none is missing: the endpoint returns live, active bands only. A flag the
 * caller has to remember to filter on is a flag some caller will forget — and forgetting it here
 * would place a student in a band their tenant has retired.
 *
 * ⚠ `orderNo` IS PART OF THE RULE, not display data. Overlapping bands resolve by catalogue order
 * (`deriveAgeCategoryId` takes the FIRST match), so a list re-sorted by anything else places people
 * differently from every authenticated screen. The endpoint already returns them in that order; do
 * not re-sort.
 *
 * This shape satisfies `AgeBand` from `@zercode/forms`, which is what lets the signup form call the
 * SAME `derivePlacements` the profile screens call rather than carrying a second copy of the rule.
 */
export const publicAgeCategorySchema = z.object({
  id: z.guid(),
  name: z.string(),
  minAge: z.number(),
  maxAge: z.number(),
  programTypeId: z.guid(),
  orderNo: z.number()
});

export const publicAgeCategoryListSchema = z.array(publicAgeCategorySchema);

export type PublicAgeCategory = z.infer<typeof publicAgeCategorySchema>;

/**
 * SR: the three anonymous auth endpoints all return an enumeration-safe status envelope
 * ({ status: "verification_sent" | "verified" }). Kept lenient (z.string()) so a wording
 * change on the backend does not break parsing.
 */
export const registerResultSchema = z.object({ status: z.string() });
/**
 * LINK-SIGNIN: `tokens` is present when consuming the link ALSO opened a session, so the page can land
 * the user IN the app instead of showing them a sign-in button.
 *
 * ⚠ ABSENT IS A NORMAL OUTCOME, NOT A FAILURE — the server withholds a session for an MFA user (the
 * link would otherwise be a second-factor bypass) and when the tenant is at its concurrent-session
 * limit. Both still verify the address, so `status` stays "verified" either way and the token pair is
 * the ONLY signal to branch on. Optional rather than required for the same reason: a server that has
 * not been deployed yet simply returns the old shape.
 */
export const verifyEmailResultSchema = z.object({
  status: z.string(),
  tokens: tokenPairSchema.nullish()
});
export const resendVerificationResultSchema = z.object({ status: z.string() });

export type RegisterResult = z.infer<typeof registerResultSchema>;
export type VerifyEmailResult = z.infer<typeof verifyEmailResultSchema>;
export type ResendVerificationResult = z.infer<typeof resendVerificationResultSchema>;

/**
 * TCH-INV: the ACTIVATION surface for an INVITED user — someone whose account was created for them
 * and who therefore has no password yet. Distinct from verify-email, where the account already has
 * one: consuming an activation link SETS the first password, so the two token kinds are separated
 * server-side by `purpose` (mig 289) and must not be mixed here either.
 *
 * The lookup answers 200 with valid=false for an unusable link (never a 404) so probing a token
 * yields no signal; displayName/maskedEmail are only present when valid.
 */
export const activationInfoSchema = z.object({
  valid: z.boolean(),
  displayName: z.string().nullish(),
  maskedEmail: z.string().nullish()
});
/** LINK-SIGNIN: same contract as {@link verifyEmailResultSchema} — see the note there. */
export const activationResultSchema = z.object({
  status: z.string(),
  tokens: tokenPairSchema.nullish()
});

export type ActivationInfo = z.infer<typeof activationInfoSchema>;
export type ActivationResult = z.infer<typeof activationResultSchema>;

/**
 * PWD-RESET (mig 446): the SELF-SERVICE PASSWORD RESET surface. The exact INVERSE of activation —
 * that one sets the FIRST password of an account that has none, this one REPLACES the password of an
 * account that has one — so the server refuses each token kind at the other's endpoint (`purpose`,
 * mig 289) and the two must not be mixed here either.
 *
 * ⚠ `maskedEmail` and nothing else. There is deliberately no display name, unlike the activation
 * lookup: an invitee needs to be told whose account they were invited to, whereas a reset is
 * requested BY the account holder, who needs only to recognise the address. Less for a leaked link
 * to reveal.
 *
 * The lookup answers 200 with valid=false for an unusable link (never a 404), so probing a token
 * yields no signal.
 */
export const passwordResetInfoSchema = z.object({
  valid: z.boolean(),
  maskedEmail: z.string().nullish()
});

/**
 * `{ status: "reset_requested" | "password_reset" }`, kept lenient like its siblings.
 *
 * ⚠ "reset_requested" IS NOT A CONFIRMATION THAT MAIL WAS SENT, and the UI must not word it as one.
 * The server returns exactly this whether or not an account owns the address — that is what stops the
 * endpoint being an account-existence oracle, and copy saying "we've sent you an email" would leak
 * the very thing the status is flattened to hide.
 */
export const passwordResetResultSchema = z.object({ status: z.string() });

export type PasswordResetInfo = z.infer<typeof passwordResetInfoSchema>;
export type PasswordResetResult = z.infer<typeof passwordResetResultSchema>;
